import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from 'react-i18next';
import {
  BiArrowToRight,
  BiAt,
  BiBrain,
  BiFastForwardCircle,
  BiFile,
  BiLinkExternal,
  BiLogoJavascript,
  BiMessageRoundedDots,
  BiMicrophone,
  BiPaperclip,
  BiShuffle,
  BiSolidBot,
  BiSolidExit,
  BiSolidImageAlt,
  BiSolidMegaphone,
  BiSolidShapes,
  BiSolidTimeFive,
  BiToggleLeft,
  BiUndo,
  BiUserVoice,
  BiVideo,
  BiX,
} from 'react-icons/bi';
import { FaListAlt } from 'react-icons/fa';
import { MdCompress } from 'react-icons/md';

import { useAppSelector } from '@/app/hook';
import { PSButton } from '@/components/ui/button';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

import { MakeIcon } from '../../common/icons/make.icon';
import { SuperUserManagerIcon } from '../../common/icons/superUserManager.icon';
import VariableIcon from '../../common/icons/variable.icon';
import PopoverWithAudio from '../../common/PopoverWithAudio';
import ScenarioYoutubeVideo from '../../common/ScenarioYoutubeVideo';
import useMacBotActions from './slice/useMacBotActions';
import { MacBotActionKind, YoutubePosition } from './types';

interface scenarioHeaderProps {
  onClose: () => void;
  type: MacBotActionKind;
  scenarioType?: BotTypeEnum;
}

const ScenarioHeader = ({ onClose, type, scenarioType }: scenarioHeaderProps) => {
  const { t } = useTranslation();
  const isVideo = scenarioType == BotTypeEnum.VIDEO_SCENARIO;

  const currentScenarioId = useAppSelector(state => state.bots.wizard.id);
  const { closeForm } = useMacBotActions(currentScenarioId);

  const HEADER_CONFIG: Record<
    MacBotActionKind,
    {
      title: string;
      tooltip: string;
      youtube: string;
      icon: JSX.Element | undefined;
      tooltipImage: string;
      description: string;
    }
  > = {
    [MacBotActionKind.ShowGotoForm]: {
      title: ReactHtmlParser(t('MacBotGotoForm/title')),
      tooltip: t('MacBotGotoForm/Tooltips'),
      youtube: 'GO_TO',
      icon: <BiUndo className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25012813145815_Go_to_a_block-min.png',
      description: t('MacBotGotoForm/MacBotGotoForm'),
    },
    [MacBotActionKind.ShowAttachmentForm]: {
      title: ReactHtmlParser(t('MacBotAttachmentForm/key1')),
      tooltip: t('MacBotAttachmentForm/Tooltips'),
      youtube: 'SEND_ATTACHMENT',
      icon: <BiPaperclip className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25012813125758_Bot_Attachment-min.png',
      description: t('MacBotAttachmentForm/description'),
    },
    [MacBotActionKind.ShowButtonForm]: {
      title: ReactHtmlParser(t('MacBotButtonForm/title')),
      tooltip: t('MacBotButtonForm/Tooltips'),
      youtube: 'ADD_BUTTON',
      icon: <BiToggleLeft className="tw-w-6 tw-h-6 tw-relative  tw-text-gray-800" />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25012813125879_Button-min.png',
      description: isVideo ? t('MacBotButtonForm/videoDescription') : t('MacBotButtonForm/description'),
    },
    [MacBotActionKind.ShowSummarizeConversation]: {
      title: ReactHtmlParser(t('SummarizeConversation/Title')),
      tooltip: t('SummarizeConversation/Description'),
      youtube: 'SUMMARIZE_CONVERSATION',
      icon: <MdCompress className="tw-w-6 tw-h-6 tw-relative  tw-text-gray-800" />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/2505291332517_sum_conversation.png',
      description: isVideo ? t('SummarizeConversation/TooltipsDescription') : t('SummarizeConversation/TooltipsDescription'),
    },
    [MacBotActionKind.ShowConditionForm]: {
      title: ReactHtmlParser(t('MacBotConditionForm/key1')),
      tooltip: t('MacBotConditionForm/Tooltips'),
      youtube: 'CONDITIONS',
      icon: <BiShuffle className="tw-w-6 tw-h-6 tw-relative  tw-text-gray-800" />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25012813125832_Conditions-min.png',
      description: t('MacBotConditionForm/description'),
    },
    [MacBotActionKind.ShowCreateVariableForm]: {
      title: ReactHtmlParser(t('MacBotCreateVariableForm/key1')),
      tooltip: t('MacBotCreateVariableForm/Tooltips'),
      youtube: 'SEND_ATTACHMENT',
      icon: <VariableIcon />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/2501281312586_Create_Variable-min.png',
      description: t('MacBotCreateVariableForm/description'),
    },
    [MacBotActionKind.ShowDelayForm]: {
      title: ReactHtmlParser(t('MacDelayForm/key1')),
      tooltip: t('MacDelayForm/Tooltips'),
      youtube: 'DELAY',
      icon: <BiSolidTimeFive className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25012813125733_Add_Delay-min.png',
      description: t('MacDelayForm/description'),
    },
    [MacBotActionKind.ShowJSCode]: {
      title: ReactHtmlParser(t('JSCode/Title')),
      tooltip: t('JSCode/Description'),
      youtube: 'JSCODE',
      icon: <BiLogoJavascript className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/2505291835259_js_code.png',
      description: t('JSCode/TooltipsDescription'),
    },
    [MacBotActionKind.ShowEndOfConversationForm]: {
      title: ReactHtmlParser(t('MacEndConversation/key1')),
      tooltip: t('MacEndConversation/Tooltips'),
      youtube: 'END_OF_CONVERSATION',
      icon: <BiSolidExit className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25012813125888_End_of_Conversation-min.png',
      description: t('MacEndConversation/description'),
    },
    [MacBotActionKind.ShowHTTPRequestForm]: {
      title: ReactHtmlParser(t('McBotHttpForm/key3')),
      tooltip: t('McBotHttpForm/Tooltips'),
      icon: <BiSolidMegaphone className="tw-w-6 tw-h-6 tw-relative" />,
      youtube: 'HTTP_REQUEST',
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25012813145844_Make_a_HTTP_Request-min.png',
      description: t('McBotHttpForm/description'),
    },
    [MacBotActionKind.ShowMakeWebHookForm]: {
      title: ReactHtmlParser(t('MacMakeCom/key1')),
      tooltip: t('MacMakeCom/Tooltips'),
      youtube: 'MAKE_WEBHOOK',
      icon: <MakeIcon />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25012813145844_Make_a_HTTP_Request-min.png',
      description: t('AddItemModal/description'),
    },
    [MacBotActionKind.ShowLeadForm]: {
      title: ReactHtmlParser(t('MacBotLeadForm/key1')),
      tooltip: t('MacBotLeadForm/Tooltips'),
      youtube: 'LEAD_FORM',
      icon: <FaListAlt className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25012813145830_Lead_Form-min.png',
      description: t('MacBotLeadForm/description'),
    },
    [MacBotActionKind.ShowMediaForm]: {
      title: t('MacBotMediaForm/key1'),
      tooltip: t('MacBotMediaForm/Tooltips'),
      youtube: 'SEND_MEDIA',
      icon: <BiSolidImageAlt className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25012813125777_Bot_Media-min.png',
      description: t('MacBotMediaForm/description'),
    },
    [MacBotActionKind.ShowOverrideVariableForm]: {
      title: ReactHtmlParser(t('MacBotOverrideVariableForm/key1')),
      tooltip: t('MacBotOverrideVariableForm/Tooltips'),
      youtube: 'OVERRIDE_VARIABLE',
      icon: <BiArrowToRight className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25012813145889_Override_Variable-min.png',
      description: t('MacBotOverrideVariableForm/description'),
    },
    [MacBotActionKind.ShowResponseForm]: {
      title: isVideo ? ReactHtmlParser(t('VideoMacBotResponseForm/title')) : ReactHtmlParser(t('MacBotResponseForm/title')),
      tooltip: t('MacBotResponseForm/Tooltips'),
      youtube: isVideo ? 'VIDEOBOT_MESSAGE' : 'BOT_MESSAGE',
      icon: isVideo ? (
        <BiVideo className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />
      ) : (
        <BiSolidBot className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />
      ),
      tooltipImage: isVideo
        ? 'https://cdn.1cdn.app/application/LOUISEBOT/25012813145961_Video_Bot_Message-min.png'
        : 'https://cdn.1cdn.app/application/LOUISEBOT/25012813125789_Bot_Message-min.png',
      description: isVideo ? t('VideoMacBotResponseForm/description') : t('MacBotResponseForm/description'),
    },
    [MacBotActionKind.ShowUserInputForm]: {
      title: ReactHtmlParser(t('MacBotUserInputForm/title')),
      tooltip: t('MacBotUserInputForm/Tooltips'),
      youtube: 'READ_USER_MESSAGE',
      icon: <BiUserVoice className="tw-w-6 tw-h-6 tw-relative" />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25012813145815_Read_User_Messages-min.png',
      description: t('MacBotUserInputForm/description'),
    },
    [MacBotActionKind.ShowSpeechRecording]: {
      title: ReactHtmlParser(t('AddItemModal/key18')),
      tooltip: t('AddItemModal/desc18'),
      youtube: 'SPEECH_RECORDING',
      icon: <BiMicrophone />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25052815374741_voice_message.png',
      description: t('AddItemModal/desc19'),
    },
    [MacBotActionKind.ShowVariableForm]: {
      title: ReactHtmlParser(t('MacBotCreateVariableForm/key10')),
      tooltip: t('MacBotVariableForm/Tooltips'),
      youtube: 'ASSIGN_VARIABLE',
      icon: <BiSolidShapes className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25012813125746_Assign_Variables-min.png',
      description: t('MacBotVariableForm/description'),
    },
    [MacBotActionKind.ShowLINKForm]: {
      title: ReactHtmlParser(t('MacBotLinkForm/title')),
      tooltip: t('MacBotLinkForm/Tooltips'),
      youtube: 'ADD_LINK',
      icon: <BiLinkExternal className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />,
      tooltipImage: isVideo
        ? 'https://cdn.1cdn.app/application/LOUISEBOT/25012813145955_Video_Button-min.png'
        : 'https://cdn.1cdn.app/application/LOUISEBOT/25012813145790_External_Link-min.png',
      description: isVideo ? t('MacBotLinkForm/videoDescription') : t('MacBotLinkForm/description'),
    },
    [MacBotActionKind.ShowElseForm]: {
      title: '',
      tooltip: '',
      youtube: '',
      icon: undefined,
      tooltipImage: '',
      description: '',
    },
    [MacBotActionKind.ShowNotifyMeByEmail]: {
      title: ReactHtmlParser(t('MacNotifyEmail/key1')),
      tooltip: t('MacNotifyEmail/Tooltips'),
      icon: <BiAt className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />,
      description: t('MacNotifyEmail/key1'),
      youtube: 'NOTIFY_ME_BY_EMAIL',
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25021112463364_notify_me_by_email-min.png',
    },
    [MacBotActionKind.CloseForm]: {
      title: '',
      tooltip: '',
      youtube: '',
      icon: undefined,
      tooltipImage: '',
      description: '',
    },
    [MacBotActionKind.ShowReceiveAttachment]: {
      title: ReactHtmlParser(t('MacReceiveFile/key1')),
      tooltip: t('MacReceiveFile/Tooltips'),
      icon: <BiFile size={22} className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />,
      description: t('MacReceiveFile/description'),
      youtube: 'RECEIVE_ATTACHMENT',
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25021112463349_receive_file-min.png',
    },
    [MacBotActionKind.ShowPassToAi]: {
      title: ReactHtmlParser(t('PassToAI/Title')),
      tooltip: t('PassToAI/Tooltips'),
      icon: <BiBrain size={22} className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />,
      description: t('PassToAI/ToolDescription'),
      youtube: 'PASS_TO_AI',
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25041919053840_image_pass_to_AI.png',
    },
    [MacBotActionKind.ShowAskToAI]: {
      title: ReactHtmlParser(t('AskToAI/Title')),
      tooltip: t('AskToAI/Description'),
      icon: <BiMessageRoundedDots size={22} className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />,
      description: t('AskToAI/ToolDescription'),
      youtube: 'PASS_TO_AI',
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25052909215678_ask_to_ia.png',
    },
    [MacBotActionKind.ShowSwitchToChatbot]: {
      title: ReactHtmlParser(t('SwitchToChatbot/Title')),
      tooltip: t('SwitchToChatbot/Tooltips'),
      icon: <BiFastForwardCircle size={22} className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />,
      description: t('SwitchToChatbot/ToolDescription'),
      youtube: 'SWITCH_TO_CHATBOT',
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25041919150785_switch_to_other_bot.png',
    },
    [MacBotActionKind.ShowSuperUserManagerServices]: {
      title: ReactHtmlParser(t('SuperUserManager/key1')),
      tooltip: t('SuperUserManager/connect/Tooltips'),
      description: t('SuperUserManager/connect/ToolDescription'),
      youtube: 'SUPER_USER_MANAGER_SERVICES',
      icon: <SuperUserManagerIcon />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25041516381835_get_profile.png',
    },
    [MacBotActionKind.ShowCheckIfUserExists]: {
      title: ReactHtmlParser(t('SuperUserManager/checkUserExists')),
      tooltip: t('SuperUserManager/checkExist/Tooltips'),
      description: t('SuperUserManager/checkExist/ToolDescription'),
      youtube: 'CHECK_IF_USER_EXISTS',
      icon: <SuperUserManagerIcon />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25041516381834_check_if_exist.png',
    },
    [MacBotActionKind.ShowUserCredential]: {
      title: ReactHtmlParser(t('SuperUserManager/userCredential')),
      tooltip: t('SuperUserManager/loginForm/Tooltips'),
      description: t('SuperUserManager/loginForm/ToolDescription'),
      youtube: 'USER_CREDENTIAL',
      icon: <SuperUserManagerIcon />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25041516381898_login.png',
    },
    [MacBotActionKind.ShowGetUserInfo]: {
      title: ReactHtmlParser(t('SuperUserManager/getUserInfo')),
      tooltip: t('SuperUserManager/getInfo/Tooltips'),
      description: t('SuperUserManager/getInfo/ToolDescription'),
      youtube: 'GET_USER_INFO',
      icon: <SuperUserManagerIcon />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25041516381835_get_profile.png',
    },
    [MacBotActionKind.ShowRegisterUser]: {
      title: ReactHtmlParser(t('SuperUserManager/key3')),
      tooltip: t('SuperUserManager/registerForm/Tooltips'),
      description: t('SuperUserManager/registerForm/ToolDescription'),
      youtube: 'REGISTER_USER',
      icon: <SuperUserManagerIcon />,
      tooltipImage: 'https://cdn.1cdn.app/application/LOUISEBOT/25041516381887_register.png',
    },
  };

  const { title, tooltip, youtube, icon, tooltipImage, description } = HEADER_CONFIG[type] || {
    title: 'Unknown',
    tooltip: 'Unknown',
    icon: undefined,
  };

  return (
    <div className="tw-h-14 tw-p-2 tw-justify-between tw-items-center tw-inline-flex tw-w-full">
      {title !== 'Unknown' && (
        <div className="tw-flex tw-items-center tw-rounded-md tw-cursor-pointer tw-justify-center tw-whitespace-nowrap tw-h-[38px]  tw-px-4 tw-py-2 tw-bg-white tw-shadow-sm tw-border tw-border-gray-300 tw-gap-2 tw-border-solid  tw-p-2.5 sm:tw-pl-3 sm:tw-pr-4 sm:tw-py-2.5">
          {icon}
          <div className="tw-justify-start tw-items-center tw-gap-1 tw-flex">
            <div className="tw-text-gray-800 tw-text-sm tw-font-semibold tw-font-['Lexend'] tw-leading-tight">{title}</div>
            <PopoverWithAudio
              type="ADVANCED"
              data={{
                size: 20,
                contentText: tooltip,
                popupImageUrl: tooltipImage,
                description: description,
              }}
            />
          </div>
        </div>
      )}

      <div className="tw-justify-end tw-items-center tw-gap-2 tw-flex tw-h-14">
        {title !== 'Unknown' && <ScenarioYoutubeVideo item={{ name: youtube }} type={YoutubePosition.ScenarioHeader} />}

        <PSButton
          id="btn-close-scenario-header"
          onClick={() => {
            onClose();
            closeForm();
          }}
          variant="none"
          className="tw-bg-white tw-shadow-sm tw-border-gray-300 tw-border tw-border-solid"
          type="button"
        >
          <BiX className="tw-w-6 tw-h-6 tw-relative tw-overflow-hidden" />
        </PSButton>
      </div>
    </div>
  );
};

export default ScenarioHeader;
